import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  FolderKanban, 
  Clock, 
  DollarSign, 
  AlertCircle,
  Calendar,
  User,
  Wrench,
  FileText,
  Loader2
} from 'lucide-react';

const FIELDMARSHAL_API = process.env.REACT_APP_FIELDMARSHAL_API || 'https://techlog-1.emergent.host/api/public';

export default function FieldMarshalPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [issues, setIssues] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [costEntries, setCostEntries] = useState([]);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    fetchTechLogData();
  }, []);

  const fetchTechLogData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all projects first
      const projectsRes = await axios.get(`${FIELDMARSHAL_API}/projects`);
      const projects = projectsRes.data.projects || [];
      
      if (projects.length === 0) {
        setError('No projects found');
        return;
      }

      // Use the first project (6400 Grinder Project)
      const projectId = projects[0].id;
      
      // Fetch full project data with all related entries
      const fullRes = await axios.get(`${FIELDMARSHAL_API}/projects/${projectId}/full`);
      const fullData = fullRes.data;

      setProject(fullData.project);
      setIssues(fullData.issues || []);
      setTimeEntries(fullData.time_entries || []);
      setCostEntries(fullData.cost_entries || []);
      setSummary(fullData.summary);

    } catch (err) {
      console.error('Failed to fetch TechLog data:', err);
      setError('Failed to load project data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatHours = (hours) => {
    if (!hours) return '0h';
    return `${hours.toFixed(1)}h`;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading Field Marshal data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <Card className="border-red-500/30 bg-red-500/5">
          <CardContent className="p-6 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400" />
            <p className="text-red-400">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="fieldmarshal-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
            Field Marshal
          </h1>
          <p className="text-muted-foreground mt-1">
            Project tracking and cost management
          </p>
        </div>
      </div>

      {/* Current Project Card */}
      {project && (
        <Card className="border-primary/30 bg-primary/5" data-testid="project-card">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                  <Wrench className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <CardTitle className="text-xl">{project.name}</CardTitle>
                  <CardDescription className="mt-1">{project.description}</CardDescription>
                </div>
              </div>
              <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                {project.template_type?.replace('_', ' ') || 'Project'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Target:</span>
                <span className="font-medium">{formatDate(project.target_date)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Issues:</span>
                <span className="font-medium">{summary?.issue_count || 0}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Created:</span>
                <span className="font-medium">{formatDate(project.created_at)}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Updated:</span>
                <span className="font-medium">{formatDate(project.updated_at)}</span>
              </div>
            </div>

            {/* Issues */}
            {issues.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <h4 className="text-sm font-medium text-muted-foreground mb-2">Active Issues</h4>
                <div className="space-y-2">
                  {issues.map((issue) => (
                    <div key={issue.id} className="flex items-center justify-between p-3 rounded-lg bg-background/50 border border-border">
                      <div className="flex items-center gap-3">
                        <AlertCircle className={`w-4 h-4 ${issue.severity === 'critical' ? 'text-red-400' : issue.severity === 'high' ? 'text-orange-400' : 'text-yellow-400'}`} />
                        <span className="font-medium">{issue.title}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={issue.severity === 'critical' ? 'destructive' : 'secondary'}>
                          {issue.severity}
                        </Badge>
                        <Badge variant="outline">{issue.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Hours */}
        <Card data-testid="hours-summary-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Hours</p>
                <p className="text-3xl font-bold text-foreground mt-1">
                  {formatHours(summary?.total_hours || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {timeEntries.length} time entries recorded
            </p>
          </CardContent>
        </Card>

        {/* Labor Cost */}
        <Card data-testid="labor-cost-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Labor Cost</p>
                <p className="text-3xl font-bold text-emerald-400 mt-1">
                  {formatCurrency(summary?.total_labor_cost || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-emerald-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Based on logged time entries
            </p>
          </CardContent>
        </Card>

        {/* Material Cost */}
        <Card data-testid="material-cost-card">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Material Cost</p>
                <p className="text-3xl font-bold text-amber-400 mt-1">
                  {formatCurrency(summary?.total_material_cost || 0)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <FolderKanban className="w-6 h-6 text-amber-400" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              {costEntries.length} cost entries recorded
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Time Entries Table */}
      <Card data-testid="time-entries-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5" />
            Time Entries
          </CardTitle>
          <CardDescription>Logged work hours for this project</CardDescription>
        </CardHeader>
        <CardContent>
          {timeEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No time entries recorded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Description</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Hours</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {timeEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4 text-sm">{formatDate(entry.date || entry.created_at)}</td>
                      <td className="py-3 px-4 text-sm">{entry.description || '-'}</td>
                      <td className="py-3 px-4 text-sm">{entry.user_name || '-'}</td>
                      <td className="py-3 px-4 text-sm text-right font-medium">{formatHours(entry.hours)}</td>
                      <td className="py-3 px-4 text-sm text-right text-emerald-400">{formatCurrency(entry.cost)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cost Entries Table */}
      <Card data-testid="cost-entries-table">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Cost Entries
          </CardTitle>
          <CardDescription>Material and other costs for this project</CardDescription>
        </CardHeader>
        <CardContent>
          {costEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <DollarSign className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No cost entries recorded yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Date</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Category</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Description</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Vendor</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {costEntries.map((entry) => (
                    <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/30">
                      <td className="py-3 px-4 text-sm">{formatDate(entry.date || entry.created_at)}</td>
                      <td className="py-3 px-4 text-sm">
                        <Badge variant="outline">{entry.category || 'Other'}</Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">{entry.description || '-'}</td>
                      <td className="py-3 px-4 text-sm">{entry.vendor || '-'}</td>
                      <td className="py-3 px-4 text-sm text-right font-medium text-amber-400">{formatCurrency(entry.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
